import { useState, type ReactNode } from "react";

interface CollapsibleSectionProps {
  label: string;
  children: ReactNode;
  defaultOpen?: boolean;
  storageKey?: string;
}

function readStored(key: string, defaultOpen: boolean): boolean {
  try {
    const value = localStorage.getItem(key);
    if (value === "0") return false;
    if (value === "1") return true;
  } catch {
    // localStorage may be unavailable
  }
  return defaultOpen;
}

export function CollapsibleSection({
  label,
  children,
  defaultOpen = true,
  storageKey,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(() =>
    storageKey ? readStored(storageKey, defaultOpen) : defaultOpen,
  );

  const toggle = () => {
    setOpen((current) => {
      const next = !current;
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, next ? "1" : "0");
        } catch {
          // ignore
        }
      }
      return next;
    });
  };

  return (
    <section style={styles.section}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        style={styles.header}
      >
        {label}
        <span
          aria-hidden
          style={{
            ...styles.chevron,
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
          }}
        >
          ›
        </span>
      </button>
      {open && children}
    </section>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: 4,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
    width: "100%",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "lowercase",
    color: "var(--fg-3)",
    margin: "0 0 10px",
    padding: "0 0 6px",
    borderBottom: "1px solid var(--comp-rule)",
    background: "none",
    borderTop: "none",
    borderLeft: "none",
    borderRight: "none",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
  },
  chevron: {
    display: "inline-block",
    fontSize: 12,
    lineHeight: 1,
    color: "var(--fg-mute)",
    transition: "transform 0.15s ease",
  },
};
